/** 2D vector math — pure helpers shared by client and server. */

export interface Vec2 {
  x: number;
  y: number;
}

export const TAU = Math.PI * 2;

export function vec2(x: number, y: number): Vec2 {
  return { x, y };
}

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(v: Vec2, s: number): Vec2 {
  return { x: v.x * s, y: v.y * s };
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

export function lenSq(v: Vec2): number {
  return v.x * v.x + v.y * v.y;
}

// NOTE: sqrt(x²+y²) everywhere instead of Math.hypot — Math.sqrt is the only
// Math function whose IEEE 754 rounding is mandated by ECMA-262, so it is
// bit-identical across JS engines. This library runs inside the deterministic
// simulation replayed on both client and server.

export function len(v: Vec2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

export function distSq(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export function dist(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Unit vector; falls back to `fallback` (default +x) when `v` is near zero. */
export function normalize(v: Vec2, fallback: Vec2 = { x: 1, y: 0 }): Vec2 {
  const l = Math.sqrt(v.x * v.x + v.y * v.y);
  if (l < 1e-9) return { x: fallback.x, y: fallback.y };
  return { x: v.x / l, y: v.y / l };
}

/** Left-hand perpendicular (rotates +90° in a y-down coordinate system). */
export function perp(v: Vec2): Vec2 {
  return { x: -v.y, y: v.x };
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function lerpVec(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Wraps an angle to (-π, π]. */
export function wrapAngle(a: number): number {
  let r = a % TAU;
  if (r <= -Math.PI) r += TAU;
  else if (r > Math.PI) r -= TAU;
  return r;
}

/** Interpolates between two angles along the shortest arc. */
export function lerpAngle(a: number, b: number, t: number): number {
  return wrapAngle(a + wrapAngle(b - a) * t);
}

/** Closest point on segment [a,b] to p, returned with its parameter t ∈ [0,1]. */
export function projectOnSegment(p: Vec2, a: Vec2, b: Vec2): { point: Vec2; t: number } {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const l2 = abx * abx + aby * aby;
  if (l2 < 1e-12) return { point: { x: a.x, y: a.y }, t: 0 };
  const t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / l2, 0, 1);
  return { point: { x: a.x + abx * t, y: a.y + aby * t }, t };
}
